/**
 * MACIRI DONIA — Collection card image slider
 * Vanilla JS: independent autoplay, infinite loop, touch + mouse drag, keyboard.
 */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 48;
  var DRAG_CLICK_THRESHOLD = 6;

  /**
   * Read section-level slider config from nearest [data-collection-section].
   */
  function getSectionConfig(root) {
    var section = root.closest('[data-collection-section]');
    if (!section) {
      return {
        autoplay: true,
        autoplaySpeed: 3500,
        resumeDelay: 5000,
        showArrows: true,
        showDots: true,
        transition: 'slide'
      };
    }

    return {
      autoplay: section.dataset.autoplay !== 'false',
      autoplaySpeed: parseInt(section.dataset.autoplaySpeed, 10) || 3500,
      resumeDelay: parseInt(section.dataset.autoplayResume, 10) || 5000,
      showArrows: section.dataset.showArrows !== 'false',
      showDots: section.dataset.showDots !== 'false',
      transition: section.dataset.transition || 'slide'
    };
  }

  /**
   * Per-product-card slider instance.
   */
  function CollectionCardSlider(root) {
    this.root = root;
    this.config = getSectionConfig(root);
    this.track = root.querySelector('[data-slider-track]');
    this.realSlides = this.track
      ? Array.prototype.slice.call(this.track.querySelectorAll('[data-slider-slide]'))
      : [];
    this.prevBtn = root.querySelector('[data-slider-prev]');
    this.nextBtn = root.querySelector('[data-slider-next]');
    this.dotsContainer = root.querySelector('[data-slider-dots]');

    if (!this.track || this.realSlides.length < 2) return;

    this.realCount = this.realSlides.length;
    this.isFade = this.config.transition === 'fade';
    this.index = 0;
    this.visualIndex = this.isFade ? 0 : 1;
    this.isDragging = false;
    this.didDrag = false;
    this.startX = 0;
    this.currentX = 0;
    this.autoplayTimer = null;
    this.resumeTimer = null;
    this.isPaused = false;
    this.isHovered = false;
    this.isTouching = false;
    this.transitioning = false;

    root.dataset.transition = this.config.transition;
    this.setupInfinite();
    this.buildDots();
    this.bindEvents();
    this.updateUI(false);
    this.startAutoplay();
  }

  CollectionCardSlider.prototype.setupInfinite = function () {
    if (this.isFade) return;

    var firstClone = this.realSlides[0].cloneNode(true);
    var lastClone = this.realSlides[this.realCount - 1].cloneNode(true);

    firstClone.setAttribute('data-slider-clone', 'first');
    lastClone.setAttribute('data-slider-clone', 'last');
    firstClone.setAttribute('aria-hidden', 'true');
    lastClone.setAttribute('aria-hidden', 'true');

    var cloneImgs = firstClone.querySelectorAll('img');
    Array.prototype.forEach.call(cloneImgs, function (img) {
      img.loading = 'lazy';
      img.removeAttribute('loading');
      img.setAttribute('loading', 'lazy');
    });
    cloneImgs = lastClone.querySelectorAll('img');
    Array.prototype.forEach.call(cloneImgs, function (img) {
      img.loading = 'lazy';
      img.removeAttribute('loading');
      img.setAttribute('loading', 'lazy');
    });

    this.track.insertBefore(lastClone, this.realSlides[0]);
    this.track.appendChild(firstClone);
    this.allSlides = Array.prototype.slice.call(
      this.track.querySelectorAll('[data-slider-slide], [data-slider-clone]')
    );
  };

  CollectionCardSlider.prototype.buildDots = function () {
    if (!this.config.showDots || !this.dotsContainer) return;

    this.dotsContainer.innerHTML = '';
    var self = this;

    for (var i = 0; i < this.realCount; i++) {
      (function (dotIndex) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'collection-card__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Go to image ' + (dotIndex + 1));
        dot.setAttribute('aria-selected', dotIndex === 0 ? 'true' : 'false');
        dot.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          self.goTo(dotIndex);
          self.pauseAndScheduleResume();
        });
        self.dotsContainer.appendChild(dot);
      })(i);
    }
  };

  CollectionCardSlider.prototype.getTranslatePercent = function () {
    return -this.visualIndex * 100;
  };

  CollectionCardSlider.prototype.updateUI = function (animate) {
    var self = this;

    if (this.isFade) {
      this.realSlides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === self.index);
        slide.setAttribute('aria-hidden', i === self.index ? 'false' : 'true');
      });
    } else {
      if (animate === false) {
        this.track.style.transition = 'none';
      }
      this.track.style.transform = 'translate3d(' + this.getTranslatePercent() + '%, 0, 0)';
      if (animate === false) {
        void this.track.offsetWidth;
        this.track.style.transition = '';
      }

      this.allSlides.forEach(function (slide, i) {
        slide.setAttribute('aria-hidden', i === self.visualIndex ? 'false' : 'true');
      });
    }

    if (this.dotsContainer) {
      var dots = this.dotsContainer.querySelectorAll('.collection-card__dot');
      Array.prototype.forEach.call(dots, function (dot, i) {
        var active = i === self.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }
  };

  CollectionCardSlider.prototype.normalizeIndex = function () {
    if (this.isFade) {
      this.index = (this.index + this.realCount) % this.realCount;
      this.updateUI(true);
      return;
    }

    if (this.visualIndex === 0) {
      this.visualIndex = this.realCount;
      this.index = this.realCount - 1;
      this.updateUI(false);
    } else if (this.visualIndex === this.realCount + 1) {
      this.visualIndex = 1;
      this.index = 0;
      this.updateUI(false);
    }
  };

  CollectionCardSlider.prototype.goTo = function (targetIndex, direction) {
    if (this.transitioning && !this.isDragging) return;

    var prev = this.index;
    this.index = (targetIndex + this.realCount) % this.realCount;

    if (this.isFade) {
      this.updateUI(true);
      return;
    }

    if (direction === 'next' || (direction == null && targetIndex === prev + 1)) {
      this.visualIndex += 1;
    } else if (direction === 'prev' || (direction == null && targetIndex === prev - 1)) {
      this.visualIndex -= 1;
    } else {
      this.visualIndex = this.index + 1;
    }

    this.transitioning = true;
    this.updateUI(true);
  };

  CollectionCardSlider.prototype.next = function () {
    this.goTo(this.index + 1, 'next');
  };

  CollectionCardSlider.prototype.prev = function () {
    this.goTo(this.index - 1, 'prev');
  };

  CollectionCardSlider.prototype.clearAutoplay = function () {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  CollectionCardSlider.prototype.clearResume = function () {
    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
  };

  CollectionCardSlider.prototype.startAutoplay = function () {
    var self = this;
    this.clearAutoplay();

    if (!this.config.autoplay || this.isPaused || this.isHovered || this.isTouching || this.isDragging) {
      return;
    }

    this.autoplayTimer = setInterval(function () {
      if (!self.isPaused && !self.isHovered && !self.isTouching && !self.isDragging) {
        self.next();
      }
    }, this.config.autoplaySpeed);
  };

  CollectionCardSlider.prototype.pauseAndScheduleResume = function () {
    var self = this;
    this.isPaused = true;
    this.root.classList.add('is-interacting');
    this.clearAutoplay();
    this.clearResume();

    this.resumeTimer = setTimeout(function () {
      self.isPaused = false;
      self.root.classList.remove('is-interacting');
      if (!self.isHovered && !self.isTouching && !self.isDragging) {
        self.startAutoplay();
      }
    }, this.config.resumeDelay);
  };

  CollectionCardSlider.prototype.onPointerDown = function (clientX) {
    if (this.isFade) return;
    this.isDragging = true;
    this.didDrag = false;
    this.startX = clientX;
    this.currentX = clientX;
    this.root.classList.add('is-dragging', 'is-interacting');
    this.pauseAndScheduleResume();
  };

  CollectionCardSlider.prototype.onPointerMove = function (clientX) {
    if (!this.isDragging || this.isFade) return;

    this.currentX = clientX;
    var delta = this.currentX - this.startX;

    if (Math.abs(delta) > DRAG_CLICK_THRESHOLD) {
      this.didDrag = true;
    }

    var offset = this.getTranslatePercent() + (delta / this.root.offsetWidth) * 100;
    this.track.style.transform = 'translate3d(' + offset + '%, 0, 0)';
  };

  CollectionCardSlider.prototype.onPointerUp = function () {
    if (!this.isDragging) return;

    var delta = this.currentX - this.startX;
    this.isDragging = false;
    this.root.classList.remove('is-dragging');

    if (Math.abs(delta) > SWIPE_THRESHOLD) {
      if (delta < 0) this.next();
      else this.prev();
    } else {
      this.updateUI(true);
    }

    var self = this;
    setTimeout(function () {
      self.didDrag = false;
    }, 0);
  };

  CollectionCardSlider.prototype.bindEvents = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.prev();
        self.pauseAndScheduleResume();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.next();
        self.pauseAndScheduleResume();
      });
    }

    this.track.addEventListener('transitionend', function (e) {
      if (e.target !== self.track) return;
      self.transitioning = false;
      self.normalizeIndex();
    });

    this.root.addEventListener('mouseenter', function () {
      self.isHovered = true;
      self.isPaused = true;
      self.clearAutoplay();
      self.clearResume();
      self.root.classList.add('is-interacting');
    });

    this.root.addEventListener('mouseleave', function () {
      self.isHovered = false;
      self.clearResume();
      self.resumeTimer = setTimeout(function () {
        self.isPaused = false;
        self.root.classList.remove('is-interacting');
        if (!self.isTouching && !self.isDragging) {
          self.startAutoplay();
        }
      }, self.config.resumeDelay);
    });

    this.root.addEventListener(
      'touchstart',
      function (e) {
        self.isTouching = true;
        self.pauseAndScheduleResume();
        if (!self.isFade) {
          self.onPointerDown(e.touches[0].clientX);
        }
      },
      { passive: true }
    );

    this.root.addEventListener(
      'touchmove',
      function (e) {
        if (!self.isFade) {
          self.onPointerMove(e.touches[0].clientX);
        }
      },
      { passive: true }
    );

    this.root.addEventListener(
      'touchend',
      function () {
        self.isTouching = false;
        if (!self.isFade) {
          self.onPointerUp();
        }
      },
      { passive: true }
    );

    this.root.addEventListener('mousedown', function (e) {
      if (e.button !== 0 || self.isFade) return;
      e.preventDefault();
      self.onPointerDown(e.clientX);

      function onMouseMove(ev) {
        self.onPointerMove(ev.clientX);
      }

      function onMouseUp() {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        self.onPointerUp();
      }

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });

    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.prev();
        self.pauseAndScheduleResume();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.next();
        self.pauseAndScheduleResume();
      }
    });

    Array.prototype.forEach.call(
      this.root.querySelectorAll('.collection-card__image-link'),
      function (link) {
        link.addEventListener('click', function (e) {
          if (self.didDrag) {
            e.preventDefault();
          }
        });
      }
    );
  };

  /**
   * Initialize all sliders inside a container (section or document).
   */
  function initCollectionSliders(container) {
    var scope = container || document;
    var sliders = scope.querySelectorAll('[data-card-slider]');

    Array.prototype.forEach.call(sliders, function (el) {
      if (el.__collectionSlider) return;
      var instance = new CollectionCardSlider(el);
      if (instance.track) {
        el.__collectionSlider = instance;
      }
    });
  }

  /**
   * Quick-add form: prevent double submit, basic loading state.
   */
  function initQuickAdd(container) {
    var scope = container || document;
    var forms = scope.querySelectorAll('[data-quick-add]');

    Array.prototype.forEach.call(forms, function (form) {
      if (form.__quickAddBound) return;
      form.__quickAddBound = true;

      form.addEventListener('submit', function () {
        var btn = form.querySelector('.collection-card__quick-add-btn');
        if (btn) btn.classList.add('is-loading');
      });

      form.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    });
  }

  function init(container) {
    initCollectionSliders(container);
    initQuickAdd(container);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
